/**
 * @file
 * Drupal behavior for the Rental Yield Calculator SDC.
 *
 * Handles form submission, input validation, yield calculation, and DOM updates.
 */

((Drupal, once) => {
  'use strict';

  const LOCALIZED_SPACE_PATTERN = /[\s\u00A0\u202F]/g;
  const MINIMUM_MONTHLY_RENT = 100;
  const MAXIMUM_MONTHLY_RENT = 10000;

  type MonthlyRentValidationResult =
    | { status: 'valid'; monthlyRent: number }
    | { status: 'empty' }
    | { status: 'leading_zero' }
    | { status: 'invalid' }
    | { status: 'below_minimum' }
    | { status: 'above_maximum' };

  /**
   * Rounds a number to a fixed number of decimal places.
   */
  function roundToDecimals(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  /**
   * Calculates rental yield percentage.
   *
   * Formula: (monthlyRent / price) * 12 * 100
   */
  function calculateYield(price: number, monthlyRent: number): number {
    return roundToDecimals((monthlyRent / price) * 12 * 100, 2);
  }

  /**
   * Normalises a number string using a single separator kind.
   */
  function normalizeSingleSeparatorNumber(value: string, separator: ',' | '.'): string {
    const parts = value.split(separator);

    if (parts.length === 1) {
      return value;
    }

    if (parts.slice(1).every((group) => group.length === 3)) {
      return parts.join('');
    }

    const decimalPart = parts.pop() ?? '';
    const integerPart = parts.join('');

    return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  }

  /**
   * Parses a localized numeric string.
   *
   * Accepts Continental formatting (`1.234,56`) while remaining tolerant of
   * alternate user input such as `1234.56`.
   */
  function parseLocalizedNumber(value: string): number {
    const cleaned = value.replace(/[^0-9,.\s\u00A0\u202F]/g, '').replace(LOCALIZED_SPACE_PATTERN, '');

    if (!cleaned) {
      return NaN;
    }

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');

    if (hasComma && hasDot) {
      return parseFloat(
        cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')
          ? cleaned.replace(/\./g, '').replace(',', '.')
          : cleaned.replace(/,/g, '')
      );
    }

    if (hasComma) {
      return parseFloat(normalizeSingleSeparatorNumber(cleaned, ','));
    }

    if (hasDot) {
      return parseFloat(normalizeSingleSeparatorNumber(cleaned, '.'));
    }

    return parseFloat(cleaned);
  }

  /**
   * Validates a monthly rent input string.
   */
  function validateMonthlyRentInput(value: string): MonthlyRentValidationResult {
    const trimmed = value.trim();

    if (trimmed === '') {
      return { status: 'empty' };
    }

    if (/^0/.test(trimmed)) {
      return { status: 'leading_zero' };
    }

    if (!/^[1-9]\d*$/.test(trimmed)) {
      return { status: 'invalid' };
    }

    const monthlyRent = Number(trimmed);

    if (!Number.isSafeInteger(monthlyRent)) {
      return { status: 'invalid' };
    }

    if (monthlyRent < MINIMUM_MONTHLY_RENT) {
      return { status: 'below_minimum' };
    }

    if (monthlyRent > MAXIMUM_MONTHLY_RENT) {
      return { status: 'above_maximum' };
    }

    return { status: 'valid', monthlyRent };
  }

  /**
   * Returns the validation error message for a monthly rent input result.
   */
  function getMonthlyRentErrorMessage(result: Exclude<MonthlyRentValidationResult, { status: 'valid' }>): string {
    switch (result.status) {
      case 'empty':
        return Drupal.t('Please enter a monthly rent.');
      case 'leading_zero':
        return Drupal.t('Please enter a monthly rent without leading zeros.');
      case 'invalid':
        return Drupal.t('Please enter a whole number for the monthly rent.');
      case 'below_minimum':
        return Drupal.t('Please enter a whole monthly rent of at least @minimum_monthly_rent.', {
          '@minimum_monthly_rent': String(MINIMUM_MONTHLY_RENT),
        });
      case 'above_maximum':
        return Drupal.t('Please enter a whole monthly rent of at most @maximum_monthly_rent.', {
          '@maximum_monthly_rent': String(MAXIMUM_MONTHLY_RENT),
        });
    }
  }

  /**
   * Formats a number using Continental European separators.
   */
  function formatLocalizedNumber(value: number, decimals: number): string {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  /**
   * Retrieves the numeric property price from the read-only price input.
   * Strips any non-numeric characters (currency symbol, thousands separators).
   */
  function parsePriceFromInput(input: HTMLInputElement): number {
    return parseLocalizedNumber(input.value);
  }

  /**
   * Shows an inline error message on the rent input.
   */
  function showError(rentInput: HTMLInputElement, errorEl: HTMLElement, message: string): void {
    rentInput.classList.add('is-error');
    rentInput.setAttribute('aria-invalid', 'true');
    errorEl.textContent = message;
    errorEl.removeAttribute('hidden');
    rentInput.focus();
  }

  /**
   * Clears any inline error state from the rent input.
   */
  function clearError(rentInput: HTMLInputElement, errorEl: HTMLElement): void {
    rentInput.classList.remove('is-error');
    rentInput.removeAttribute('aria-invalid');
    errorEl.textContent = '';
    errorEl.setAttribute('hidden', '');
  }

  /**
   * Updates the result area with the calculated yield.
   *
   * Writes to two targets:
   * - `liveRegionEl`: the `aria-live="polite"` region outside the hidden
   *   results panel so screen readers announce the result reliably.
   * - `visualEl`: the visible copy inside the results panel, marked
   *   `aria-hidden="true"` to prevent double-reading.
   */
  function displayResult(liveRegionEl: HTMLElement, visualEl: HTMLElement, yieldPercent: number): void {
    const formatted = `${formatLocalizedNumber(yieldPercent, 2)}%`;
    // For screen readers: contextual message
    liveRegionEl.textContent = `${Drupal.t('Estimated gross rental yield:')} ${formatted}`;
    // For visible UI: just the percentage
    visualEl.textContent = formatted;
  }

  /**
   * Updates the PDF form with the calculated percentage and amount.
   */
  function updateDownloadFormFields(resultsBlock: HTMLElement, yieldPercent: number, monthlyRent: number): void {
    const percentageInput = resultsBlock.querySelector<HTMLInputElement>('[data-yield-calculator-percentage]');
    const amountInput = resultsBlock.querySelector<HTMLInputElement>('[data-yield-calculator-amount]');

    if (!percentageInput || !amountInput) return;

    percentageInput.value = yieldPercent.toFixed(2);
    amountInput.value = monthlyRent.toFixed(2);
  }

  /**
   * Clears hidden PDF form values so stale calculations cannot be submitted.
   */
  function clearDownloadFormFields(resultsBlock: HTMLElement): void {
    const percentageInput = resultsBlock.querySelector<HTMLInputElement>('[data-yield-calculator-percentage]');
    const amountInput = resultsBlock.querySelector<HTMLInputElement>('[data-yield-calculator-amount]');

    if (percentageInput) percentageInput.value = '';
    if (amountInput) amountInput.value = '';
  }

  /**
   * Initialises one calculator instance.
   */
  function initCalculator(wrapper: Element): void {
    const form = wrapper.querySelector<HTMLFormElement>('[data-yield-calculator-form]');
    const rentInput = wrapper.querySelector<HTMLInputElement>('[data-yield-calculator-rent]');
    const errorEl = wrapper.querySelector<HTMLElement>('[data-yield-calculator-error]');
    const liveRegionEl = wrapper.querySelector<HTMLElement>('[data-yield-calculator-result]');
    const visualResultEl = wrapper.querySelector<HTMLElement>('[data-yield-calculator-result-visual]');
    const resultsBlock = wrapper.querySelector<HTMLElement>('[data-yield-calculator-results]');
    const priceInput = wrapper.querySelector<HTMLInputElement>('input[name="price"]');

    if (!form || !rentInput || !errorEl || !liveRegionEl || !visualResultEl || !resultsBlock || !priceInput) {
      return;
    }

    const price = parsePriceFromInput(priceInput);

    if (!isFinite(price) || price <= 0) {
      // Price is not usable; silently return to avoid a broken experience.
      return;
    }

    form.addEventListener('submit', (event: Event): void => {
      event.preventDefault();

      const validation = validateMonthlyRentInput(rentInput.value);

      if (validation.status !== 'valid') {
        showError(rentInput, errorEl, getMonthlyRentErrorMessage(validation));
        return;
      }

      clearError(rentInput, errorEl);
      rentInput.value = String(validation.monthlyRent);

      const yieldPercent = calculateYield(price, validation.monthlyRent);
      displayResult(liveRegionEl, visualResultEl, yieldPercent);
      updateDownloadFormFields(resultsBlock, yieldPercent, validation.monthlyRent);
      resultsBlock.removeAttribute('hidden');
    });

    // Clear error on user input so feedback is immediate.
    rentInput.addEventListener('input', (): void => {
      if (rentInput.value === '0') {
        rentInput.value = '';
      }

      if (rentInput.classList.contains('is-error')) {
        clearError(rentInput, errorEl);
      }

      if (!resultsBlock.hasAttribute('hidden')) {
        liveRegionEl.textContent = '';
        visualResultEl.textContent = '';
        clearDownloadFormFields(resultsBlock);
        resultsBlock.setAttribute('hidden', '');
      }
    });

    rentInput.addEventListener('blur', (): void => {
      const validation = validateMonthlyRentInput(rentInput.value);

      if (validation.status === 'valid') {
        rentInput.value = String(validation.monthlyRent);
      }
    });
  }

  /**
   * @type {import('../../../types/drupal').DrupalBehavior}
   */
  Drupal.behaviors.tpRentalYieldCalculator = {
    attach(context) {
      once('tp-rental-yield-calculator', '[data-drupal-component="rental-yield-calculator"]', context).forEach(
        initCalculator
      );
    },
  };
})(Drupal, once);
